import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { settingsApi } from '@/api'
import { ALL_DAYS, DAY_TO_JS, startOfWeek, type DayOfWeek, type WeekStartDay } from '@/utils/date'

export type { DayOfWeek, WeekStartDay }

const DEFAULT_SCHOOL_DAYS: DayOfWeek[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY']

// Teaching-day window — the daily hours the schedule timeline spans (mirrors Android's
// "Teaching hours" setting; default 08:00–17:00). Stored as 'HH:mm' strings.
const DEFAULT_DAY_START = '08:00'
const DEFAULT_DAY_END = '17:00'
const HHMM_RE = /^\d{1,2}:\d{2}$/

export { ALL_DAYS, DEFAULT_SCHOOL_DAYS, DEFAULT_DAY_START, DEFAULT_DAY_END }

/** Parse an 'HH:mm' string to a fractional hour (e.g. '08:30' → 8.5); fallback on bad input. */
export function hhmmToHours(value: string, fallback: number): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value)
  if (!m) return fallback
  const h = Number(m[1])
  const min = Number(m[2])
  if (h < 0 || h > 23 || min < 0 || min > 59) return fallback
  return h + min / 60
}

export function dayLabel(day: DayOfWeek): string {
  return day.charAt(0) + day.slice(1).toLowerCase()
}

export function dayShortLabel(day: DayOfWeek): string {
  return day.slice(0, 3).charAt(0) + day.slice(1, 3).toLowerCase()
}

export const useScheduleSettingsStore = defineStore('scheduleSettings', () => {
  const weekStartDay = ref<WeekStartDay>('MONDAY')
  const schoolDays = ref<DayOfWeek[]>([...DEFAULT_SCHOOL_DAYS])
  const teachingDayStart = ref<string>(DEFAULT_DAY_START)
  const teachingDayEnd = ref<string>(DEFAULT_DAY_END)
  const isLoaded = ref(false)

  /** Teaching-day window as fractional hours (drives the schedule timeline span). */
  const teachingStartHour = computed(() => hhmmToHours(teachingDayStart.value, 8))
  const teachingEndHour = computed(() => hhmmToHours(teachingDayEnd.value, 17))

  /** School days ordered by week start day */
  const orderedSchoolDays = computed(() => {
    const startIdx = ALL_DAYS.indexOf(weekStartDay.value)
    const ordered = [...ALL_DAYS.slice(startIdx), ...ALL_DAYS.slice(0, startIdx)]
    return ordered.filter(d => schoolDays.value.includes(d))
  })

  /** JS day numbers for school days, ordered */
  const schoolDayJsNumbers = computed(() => orderedSchoolDays.value.map(d => DAY_TO_JS[d]))

  /** Get the start of the week (based on weekStartDay) for a given date */
  function getWeekStart(date: Date): Date {
    return startOfWeek(date, weekStartDay.value)
  }

  /** Get the month calendar start (first visible day), aligned to week start */
  function getCalendarStart(monthStart: Date): Date {
    const d = new Date(monthStart)
    const jsStart = DAY_TO_JS[weekStartDay.value]
    const dayOfWeek = d.getDay()
    const offset = (dayOfWeek - jsStart + 7) % 7
    d.setDate(d.getDate() - offset)
    return d
  }

  /** Weekday headers in order, starting from weekStartDay */
  const weekdayHeaders = computed(() => {
    const startIdx = ALL_DAYS.indexOf(weekStartDay.value)
    return [...ALL_DAYS.slice(startIdx), ...ALL_DAYS.slice(0, startIdx)]
      .map(d => dayShortLabel(d))
  })

  async function fetchSettings() {
    try {
      const res = await settingsApi.settingsGet()
      const s = (res.data as any).settings ?? {}
      if (s['week_start_day'] === 'SUNDAY' || s['week_start_day'] === 'MONDAY') {
        weekStartDay.value = s['week_start_day']
      }
      if (s['school_days']) {
        try {
          const parsed = JSON.parse(s['school_days'])
          if (Array.isArray(parsed) && parsed.length > 0) {
            schoolDays.value = parsed.filter((d: string) => ALL_DAYS.includes(d as DayOfWeek)) as DayOfWeek[]
          }
        } catch { /* use defaults */ }
      }
      if (typeof s['teaching_day_start'] === 'string' && HHMM_RE.test(s['teaching_day_start'])) {
        teachingDayStart.value = s['teaching_day_start']
      }
      if (typeof s['teaching_day_end'] === 'string' && HHMM_RE.test(s['teaching_day_end'])) {
        teachingDayEnd.value = s['teaching_day_end']
      }
      isLoaded.value = true
    } catch {
      isLoaded.value = true
    }
  }

  async function saveSettings(
    start: WeekStartDay,
    days: DayOfWeek[],
    dayStart?: string,
    dayEnd?: string,
  ) {
    weekStartDay.value = start
    schoolDays.value = days
    if (dayStart && HHMM_RE.test(dayStart)) teachingDayStart.value = dayStart
    if (dayEnd && HHMM_RE.test(dayEnd)) teachingDayEnd.value = dayEnd
    await settingsApi.settingsPut({
      settings: {
        week_start_day: start,
        school_days: JSON.stringify(days),
        teaching_day_start: teachingDayStart.value,
        teaching_day_end: teachingDayEnd.value,
      },
    })
  }

  return {
    weekStartDay,
    schoolDays,
    teachingDayStart,
    teachingDayEnd,
    teachingStartHour,
    teachingEndHour,
    orderedSchoolDays,
    schoolDayJsNumbers,
    weekdayHeaders,
    isLoaded,
    fetchSettings,
    saveSettings,
    getWeekStart,
    getCalendarStart,
  }
})
